import config from '../config';

// Helpers for reading and formatting prices. The single source of truth for
// every displayed price is `config.pricing` — never hardcode a rate in a
// component; read it from here so one config edit updates the whole site.

// Returns the configured nightly rate (a number) for an apartment id, or null
// when no price is set (callers should fall back to a "price on request" label).
export const getNightlyPrice = (apartmentId) => {
    const amount = config.pricing?.nightly?.[apartmentId];
    return typeof amount === 'number' ? amount : null;
};

// Formats an amount in the configured currency for the given language,
// e.g. formatCurrency(120, 'en') → "€120" (no decimals).
export const formatCurrency = (amount, language) =>
    new Intl.NumberFormat(language || 'en', {
        style: 'currency',
        currency: config.pricing?.currency || 'EUR',
        maximumFractionDigits: 0,
    }).format(amount);

// Builds a Schema.org Offer for an apartment's nightly rate, or null when no
// price is set. Used in the JSON-LD structured data so schema prices come from
// the same config as the visible prices (single source of truth).
export const buildNightlyOffer = (apartmentId) => {
    const amount = getNightlyPrice(apartmentId);
    if (amount === null) return null;
    const priceCurrency = config.pricing?.currency || 'EUR';
    return {
        '@type': 'Offer',
        price: amount,
        priceCurrency,
        priceSpecification: {
            '@type': 'UnitPriceSpecification',
            price: amount,
            priceCurrency,
            unitCode: 'DAY', // UN/CEFACT code for "day" — i.e. per night
            unitText: 'night',
        },
    };
};

// Returns a human-readable price range across all configured nightly rates,
// e.g. "€165–€340" (or a single value when they are equal), or null when no
// prices are set. Used for the VacationRental schema's priceRange.
export const getPriceRange = () => {
    const amounts = Object.values(config.pricing?.nightly || {}).filter(
        (value) => typeof value === 'number'
    );
    if (amounts.length === 0) return null;
    const min = Math.min(...amounts);
    const max = Math.max(...amounts);
    return min === max
        ? formatCurrency(min, 'en')
        : `${formatCurrency(min, 'en')}–${formatCurrency(max, 'en')}`;
};
